import { useSelector } from "react-redux";
import { useCallback, useMemo, useRef, useState } from "react";
import { Col, message } from "antd";
import { differenceWith } from "lodash";
import { selectSelectedClientIds } from "../../../../redux/slices/globalSelectorSlice";
import {
  selectAvailableClients,
  selectUserId,
} from "../../../../redux/slices/userSlice";
import { ServerSideDataGrid } from "../../../../common/components/serverSideDataGrid/serverSideDataGrid";
import K from "../../../../utilities/constants";
import { getColumns } from "./columns";
import { ClientReportConfigs } from "../../../../redux/models/clientReportConfigs";
import { CreateConfigButton } from "./createConfigButton/createConfigButton";
import { DataTableLayout } from "../../../../common/components/dataTableLayout/dataTableLayout";

export const ClientReportConfigsContainer = () => {
  const selectedClientIds = useSelector(selectSelectedClientIds);
  const accessibleClients = useSelector(selectAvailableClients);
  const userId = useSelector(selectUserId);

  const gridRef = useRef(null);

  const [clientIdsWithConfigs, setClientIdsWithConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const clientsWithoutConfig = differenceWith(
    accessibleClients,
    clientIdsWithConfigs,
    (a, b) => a.id === b,
  );

  const isCreateButtonDisabled = loading || !clientsWithoutConfig.length;

  const onConfigCreated = (config) => {
    if (!clientIdsWithConfigs.length) {
      gridRef.current.api.hideOverlay();
    }

    setClientIdsWithConfigs((data) => [...data, config.clientId]);
    gridRef.current.api.applyServerSideTransaction({
      add: [config],
    });
  };

  const onConfigUpdated = (config) => {
    gridRef.current.api.applyServerSideTransaction({
      update: [config],
    });

    gridRef.current.api.refreshCells({ force: true });
  };

  const columns = useMemo(
    () => getColumns(accessibleClients, onConfigUpdated),
    [accessibleClients],
  );

  const getServerSideData = useCallback(
    (options) => {
      return ClientReportConfigs.getAll({
        ...options,
        client: selectedClientIds,
      });
    },
    [selectedClientIds],
  );

  const onDataFetchSuccess = useCallback(
    (data) => {
      setLoading(false);
      setClientIdsWithConfigs(data.rowData.map((c) => c.clientId));
    },
    [setClientIdsWithConfigs],
  );

  const onDataFetchFailed = useCallback(
    (err) => message.error(err.message),
    [],
  );

  return (
    <DataTableLayout
      searchable={false}
      actions={[
        {
          renderer: () => (
            <Col>
              <CreateConfigButton
                clients={clientsWithoutConfig}
                disabled={isCreateButtonDisabled}
                onSuccess={onConfigCreated}
              />
            </Col>
          ),
        },
      ]}
    >
      <ServerSideDataGrid
        ref={gridRef}
        userId={userId}
        configKey={K.AgGridTable.Keys.ClientReportConfigsVisibleColumn}
        getData={getServerSideData}
        defaultColumnConfig={columns}
        onDataFetchSuccess={onDataFetchSuccess}
        onDataFetchFailed={onDataFetchFailed}
      />
    </DataTableLayout>
  );
};
